import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { restaurantsApi, effectifsApi } from '../lib/api';
import { cn } from '../lib/utils';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { RestaurantSelector } from '../components/dashboard/RestaurantSelector';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tooltip as ShadcnTooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
    Users, 
    ChefHat,
    UtensilsCrossed,
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
    ChevronLeft,
    ChevronRight,
    Building2,
    TrendingUp,
    AlertTriangle,
    BarChart3
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';

// Helper pour obtenir le lundi de la semaine courante
const getMondayOfWeek = (date = new Date()) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
};

// Helper pour formater une date en français
const formatDateFr = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Helper pour formater une semaine
const formatWeekRange = (weekStart) => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    
    return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

// Card pour afficher les ETP
const EtpCard = ({ title, planned, budget, variancePct, varianceEtp, icon: Icon, color = 'primary' }) => {
    // Négatif = VERT (économie), Égal = JAUNE, Positif = ROUGE (dépassement)
    const getVarianceColor = (v) => {
        if (v === null || v === undefined) return 'text-muted-foreground';
        if (v < 0) return 'text-green-600';  // Négatif = vert (économie)
        if (v === 0) return 'text-yellow-600';  // Égal = jaune
        return 'text-red-600';  // Positif = rouge (dépassement)
    };

    const getVarianceBgColor = (v) => {
        if (v === null || v === undefined) return 'bg-muted';
        if (v < 0) return 'bg-green-50 border-green-200';
        if (v === 0) return 'bg-yellow-50 border-yellow-200';
        return 'bg-red-50 border-red-200';
    };

    const getVarianceIcon = (v) => {
        if (v === null || v === undefined) return Minus;
        if (v > 0) return ArrowUpRight;
        if (v < 0) return ArrowDownRight;
        return Minus;
    };

    const VarianceIcon = getVarianceIcon(variancePct);

    return (
        <div className="bg-card border border-border shadow-sm p-4 rounded-xl hover:shadow-md transition-all duration-200">
            <div className="flex items-start justify-between mb-3">
                <p className="text-sm text-muted-foreground font-medium">{title}</p>
                {Icon && (
                    <div className={cn("p-2 rounded-lg", 
                        color === 'cuisine' ? 'bg-orange-500/10' : 
                        color === 'service' ? 'bg-blue-500/10' : 'bg-primary/10'
                    )}>
                        <Icon className={cn("w-4 h-4", 
                            color === 'cuisine' ? 'text-orange-500' : 
                            color === 'service' ? 'text-blue-500' : 'text-primary'
                        )} />
                    </div>
                )}
            </div>
            
            <div className="space-y-2">
                <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Planifié</span>
                    <span className="text-xl font-bold text-foreground">{planned?.toFixed(2) || '0.00'} ETP</span>
                </div>
                
                <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Budget (hebdo)</span>
                    <span className="text-sm font-medium">{budget?.toFixed(2) || '0.00'} ETP</span>
                </div>
                
                <div className={cn("pt-3 mt-2 border rounded-lg p-2", getVarianceBgColor(variancePct))}>
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-muted-foreground">Écart ETP</span>
                        <div className={cn("flex items-center gap-1", getVarianceColor(varianceEtp))}>
                            <VarianceIcon className="w-3 h-3" />
                            <span className="text-sm font-bold">
                                {varianceEtp !== null && varianceEtp !== undefined 
                                    ? `${varianceEtp >= 0 ? '+' : ''}${varianceEtp.toFixed(2)} ETP` 
                                    : '-'}
                            </span>
                        </div>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Écart %</span>
                        <div className={cn("flex items-center gap-1", getVarianceColor(variancePct))}>
                            <span className="text-sm font-bold">
                                {variancePct !== null && variancePct !== undefined 
                                    ? `${variancePct >= 0 ? '+' : ''}${variancePct.toFixed(1)}%` 
                                    : '-'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Composant d'avertissement pour données potentiellement incomplètes
const DataWarningAlert = ({ comparisonData }) => {
    if (!comparisonData) return null;
    
    const warnings = [];
    
    // Vérifier si les données cuisine semblent incomplètes
    // Si budget cuisine > 5 ETP et réalisé < 50% du budget = potentiellement incomplet
    const budgetCuisine = comparisonData?.budget?.cuisine || 0;
    const plannedCuisine = comparisonData?.planned?.cuisine || 0;
    
    if (budgetCuisine > 5 && plannedCuisine < budgetCuisine * 0.5) {
        const ecartPct = ((plannedCuisine - budgetCuisine) / budgetCuisine * 100).toFixed(0);
        warnings.push({
            type: 'cuisine',
            message: `Cuisine : ${plannedCuisine.toFixed(2)} ETP planifiés vs ${budgetCuisine.toFixed(2)} ETP budgétés (${ecartPct}%). Les données Combo semblent incomplètes.`
        });
    }
    
    // Vérifier si les données service semblent incomplètes
    const budgetService = comparisonData?.budget?.service || 0;
    const plannedService = comparisonData?.planned?.service || 0;
    
    if (budgetService > 5 && plannedService < budgetService * 0.5) {
        const ecartPct = ((plannedService - budgetService) / budgetService * 100).toFixed(0);
        warnings.push({
            type: 'service',
            message: `Service : ${plannedService.toFixed(2)} ETP planifiés vs ${budgetService.toFixed(2)} ETP budgétés (${ecartPct}%). Les données Combo semblent incomplètes.`
        });
    }
    
    if (warnings.length === 0) return null;
    
    return (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4" data-testid="data-warning-alert">
            <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                    <h4 className="font-semibold text-amber-800 mb-2">
                        Données potentiellement incomplètes
                    </h4>
                    <ul className="space-y-1">
                        {warnings.map((w, i) => (
                            <li key={i} className="text-sm text-amber-700">
                                • {w.message}
                            </li>
                        ))}
                    </ul>
                    <p className="text-xs text-amber-600 mt-2">
                        Vérifiez que tous les plannings sont bien synchronisés dans Combo HR.
                    </p>
                </div>
            </div>
        </div>
    );
};

// Card YTD pour afficher la moyenne ETP du mois
const YtdEtpCard = ({ ytdData, loading }) => {
    if (loading) {
        return <Skeleton className="h-64 rounded-xl" />;
    }
    
    if (!ytdData) {
        return null;
    }
    
    const getVarianceColor = (v) => {
        if (v === null || v === undefined) return 'text-muted-foreground';
        if (v < 0) return 'text-green-600';
        if (v === 0) return 'text-yellow-600';
        return 'text-red-600';
    };
    
    const getVarianceBg = (v) => {
        if (v === null || v === undefined) return 'bg-muted';
        if (v < 0) return 'bg-green-50 border-green-200';
        if (v === 0) return 'bg-yellow-50 border-yellow-200';
        return 'bg-red-50 border-red-200';
    };
    
    const formatVariance = (value, pct) => {
        if (value === null || value === undefined) return '-';
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(2)} ETP (${sign}${pct?.toFixed(1) || 0}%)`;
    };
    
    return (
        <div className="bg-card border border-border shadow-sm p-5 rounded-xl" data-testid="ytd-etp-card">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                        <TrendingUp className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg">Encours ETP - YTD</h3>
                        <p className="text-xs text-muted-foreground">
                            Moyenne sur {ytdData.num_weeks} semaine{ytdData.num_weeks > 1 ? 's' : ''} du mois ({ytdData.month})
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Moyenne YTD */}
            <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-xs text-muted-foreground mb-1">Cuisine</p>
                    <p className="text-xl font-bold text-orange-600">{ytdData.ytd_avg?.cuisine?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground">ETP</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-muted-foreground mb-1">Service</p>
                    <p className="text-xl font-bold text-blue-600">{ytdData.ytd_avg?.service?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground">ETP</p>
                </div>
                <div className="text-center p-3 bg-primary/10 rounded-lg border border-primary/20">
                    <p className="text-xs text-muted-foreground mb-1">Total</p>
                    <p className="text-2xl font-bold text-primary">{ytdData.ytd_avg?.total?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground">ETP</p>
                </div>
            </div>
            
            {/* Comparaisons */}
            <div className="space-y-3">
                {/* vs Budget */}
                <div className={cn("p-3 rounded-lg border", getVarianceBg(ytdData.variance_vs_budget?.total))}>
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm font-medium">vs Budget</p>
                            <p className="text-xs text-muted-foreground">
                                Budget mensuel: {ytdData.budget?.total?.toFixed(2) || '0.00'} ETP
                            </p>
                        </div>
                        <div className={cn("text-right", getVarianceColor(ytdData.variance_vs_budget?.total))}>
                            <p className="font-bold">
                                {formatVariance(ytdData.variance_vs_budget?.total, ytdData.variance_vs_budget_pct?.total)}
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* vs N-1 */}
                <div className={cn("p-3 rounded-lg border", getVarianceBg(ytdData.variance_vs_n1?.total))}>
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm font-medium">vs N-1 (mois complet)</p>
                            <p className="text-xs text-muted-foreground">
                                Réalisé {ytdData.month?.split('/')[0]}/{parseInt(ytdData.month?.split('/')[1]) - 1}: {ytdData.n1_avg?.total?.toFixed(2) || '0.00'} ETP
                            </p>
                        </div>
                        <div className={cn("text-right", getVarianceColor(ytdData.variance_vs_n1?.total))}>
                            <p className="font-bold">
                                {formatVariance(ytdData.variance_vs_n1?.total, ytdData.variance_vs_n1_pct?.total)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Composant tableau "Présence des postes à responsabilité"
const ResponsibilityPresenceTable = ({ restaurantName, weekStart, isConsolidated = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        const loadData = async () => {
            if (!weekStart) return;
            if (!isConsolidated && !restaurantName) return;
            
            setLoading(true);
            setError(null);
            try {
                const response = isConsolidated 
                    ? await effectifsApi.getResponsibilityPresenceConsolidated(weekStart)
                    : await effectifsApi.getResponsibilityPresence(restaurantName, weekStart);
                setData(response.data);
            } catch (err) {
                console.error('Error loading responsibility presence:', err);
                setError('Erreur lors du chargement');
            } finally {
                setLoading(false);
            }
        };
        
        loadData();
    }, [restaurantName, weekStart, isConsolidated]);
    
    if (loading) {
        return <Skeleton className="h-32" />;
    }
    
    if (error) {
        return (
            <div className="text-center py-4 text-muted-foreground text-sm">
                {error}
            </div>
        );
    }
    
    if (!data || !data.positions || data.positions.length === 0) {
        return (
            <div className="text-center py-4 text-muted-foreground text-sm">
                {data?.message || "Aucun poste à responsabilité configuré"}
            </div>
        );
    }
    
    const days = data.days || ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
    const shortDays = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
    
    // Grouper par restaurant si consolidé
    const groupedPositions = isConsolidated 
        ? data.positions.reduce((acc, pos) => {
            const resto = pos.restaurant || 'Autre';
            if (!acc[resto]) acc[resto] = [];
            acc[resto].push(pos);
            return acc;
          }, {})
        : { [restaurantName]: data.positions };
    
    return (
        <TooltipProvider delayDuration={200}>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b-2 border-border">
                            <th className="text-left py-2 px-3 font-semibold text-muted-foreground min-w-[180px]">Nom</th>
                            <th className="text-left py-2 px-3 font-semibold text-muted-foreground min-w-[120px]">Poste</th>
                            {shortDays.map((day, idx) => (
                                <th key={day} className={cn(
                                    "text-center py-2 px-2 font-semibold w-10",
                                    idx >= 5 ? "text-orange-600" : "text-muted-foreground"
                                )}>
                                    {day}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(groupedPositions).map(([resto, positions]) => (
                            <React.Fragment key={resto}>
                                {isConsolidated && (
                                <tr className="bg-muted/50">
                                    <td colSpan={9} className="py-2 px-3 font-semibold text-sm">
                                        {resto}
                                    </td>
                                </tr>
                            )}
                            {positions.map((person, idx) => {
                                const deptColor = person.department === 'cuisine' 
                                    ? 'border-l-orange-500' 
                                    : 'border-l-blue-500';
                                
                                return (
                                    <tr key={`${person.name}-${idx}`} className={cn(
                                        "border-b border-border/50 hover:bg-muted/30",
                                        "border-l-4",
                                        deptColor
                                    )}>
                                        <td className="py-2 px-3 font-medium">{person.name}</td>
                                        <td className="py-2 px-3 text-muted-foreground text-xs">{person.position}</td>
                                        {days.map((day, dayIdx) => {
                                            const isPresent = person.presence?.[day];
                                            const isWeekend = dayIdx >= 5;
                                            const details = person.presence_details?.[day];
                                            
                                            // Construire le contenu du tooltip
                                            let tooltipContent = null;
                                            if (isPresent && details?.shifts?.length > 0) {
                                                // Présent : afficher les horaires
                                                tooltipContent = (
                                                    <div className="text-xs">
                                                        <div className="font-semibold mb-1">Présent(e)</div>
                                                        {details.shifts.map((shift, i) => (
                                                            <div key={i} className="text-muted-foreground">
                                                                {shift.start} - {shift.end || '?'}
                                                                {shift.team && <span className="ml-1 text-muted-foreground/70">({shift.team})</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            } else if (!isPresent) {
                                                // Absent : afficher la raison
                                                const absenceType = details?.absence_type;
                                                tooltipContent = (
                                                    <div className="text-xs">
                                                        <div className="font-semibold mb-1">
                                                            {absenceType || 'Repos'}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            
                                            const cellContent = isPresent ? (
                                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600 cursor-pointer hover:bg-green-200 transition-colors">
                                                    ✓
                                                </span>
                                            ) : (
                                                (() => {
                                                    const absenceType = details?.absence_type;
                                                    // Repos hebdomadaire = gris, autres absences = orange
                                                    const isWeeklyRest = absenceType === 'Repos hebdo' || absenceType === 'Repos';
                                                    const isRealAbsence = absenceType && !isWeeklyRest;
                                                    
                                                    return (
                                                        <span className={cn(
                                                            "inline-flex items-center justify-center w-6 h-6 rounded-full cursor-pointer transition-colors",
                                                            isRealAbsence
                                                                ? "bg-orange-100 text-orange-600 hover:bg-orange-200" 
                                                                : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                                                        )}>
                                                            {isRealAbsence ? "○" : "-"}
                                                        </span>
                                                    );
                                                })()
                                            );
                                            
                                            return (
                                                <td key={day} className={cn(
                                                    "text-center py-2 px-2",
                                                    isWeekend && "bg-muted/20"
                                                )}>
                                                    {tooltipContent ? (
                                                        <ShadcnTooltip>
                                                            <TooltipTrigger asChild>
                                                                {cellContent}
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="bg-popover border shadow-lg">
                                                                {tooltipContent}
                                                            </TooltipContent>
                                                        </ShadcnTooltip>
                                                    ) : cellContent}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-orange-500"></span>
                    <span>Cuisine</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-blue-500"></span>
                    <span>Salle</span>
                </div>
                <div className="flex items-center gap-1 ml-4 pl-4 border-l">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-100 text-green-600 text-[10px]">✓</span>
                    <span>Présent (survol = horaires)</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-100 text-orange-600 text-[10px]">○</span>
                    <span>Congés/Absence (survol = motif)</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 text-gray-400 text-[10px]">-</span>
                    <span>Repos hebdo (survol = détails)</span>
                </div>
            </div>
        </div>
        </TooltipProvider>
    );
};

// Tableau des ETP par jour
// Composant tableau "Présence à l'heure de pointe"
const PeakHourPresenceTable = ({ restaurantName, weekStart, isConsolidated = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        const loadData = async () => {
            if (!weekStart) return;
            if (!isConsolidated && !restaurantName) return;
            
            setLoading(true);
            setError(null);
            try {
                const response = isConsolidated 
                    ? await effectifsApi.getStaffByServiceConsolidated(weekStart)
                    : await effectifsApi.getStaffByService(restaurantName, weekStart);
                setData(response.data);
            } catch (err) {
                console.error('Error loading peak hour presence:', err);
                setError('Erreur lors du chargement des données');
            } finally {
                setLoading(false);
            }
        };
        
        loadData();
    }, [restaurantName, weekStart, isConsolidated]);
    
    if (loading) {
        return <Skeleton className="h-64" />;
    }
    
    if (error) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                {error}
            </div>
        );
    }
    
    if (!data || !data.data || Object.keys(data.data).length === 0) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                Aucune donnée disponible pour cette semaine
            </div>
        );
    }
    
    const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
    const services = data.services || ["matin", "dejeuner", "limo", "diner", "night"];
    const serviceLabels = data.service_labels || {
        "matin": "Matin (10h)",
        "dejeuner": "Déjeuner (12h30)",
        "limo": "Limo (16h)",
        "diner": "Dîner (20h30)",
        "night": "Night (00h)"
    };
    
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b-2 border-border">
                        <th className="text-left py-3 px-2 font-semibold text-muted-foreground">Jour</th>
                        {services.map(service => (
                            <th key={service} colSpan={2} className="text-center py-3 px-2 font-semibold border-l border-border">
                                {serviceLabels[service]}
                            </th>
                        ))}
                    </tr>
                    <tr className="border-b border-border bg-muted/30">
                        <th className="py-2 px-2"></th>
                        {services.map(service => (
                            <React.Fragment key={`${service}-depts`}>
                                <th className="text-center py-2 px-2 text-xs text-orange-600 border-l border-border">Cuisine</th>
                                <th className="text-center py-2 px-2 text-xs text-blue-600">Salle</th>
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {days.map((day, idx) => {
                        const dayData = data.data[day] || {};
                        const isWeekend = idx >= 5;
                        
                        return (
                            <tr key={day} className={cn(
                                "border-b border-border/50 hover:bg-muted/30",
                                isWeekend && "bg-muted/20"
                            )}>
                                <td className="py-3 px-2 font-medium">{day}</td>
                                {services.map(service => {
                                    const serviceData = dayData[service] || { cuisine: 0, salle: 0 };
                                    return (
                                        <React.Fragment key={`${day}-${service}`}>
                                            <td className={cn(
                                                "text-center py-3 px-2 border-l border-border",
                                                serviceData.cuisine > 0 ? "text-orange-600 font-medium" : "text-muted-foreground"
                                            )}>
                                                {serviceData.cuisine || '-'}
                                            </td>
                                            <td className={cn(
                                                "text-center py-3 px-2",
                                                serviceData.salle > 0 ? "text-blue-600 font-medium" : "text-muted-foreground"
                                            )}>
                                                {serviceData.salle || '-'}
                                            </td>
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {isConsolidated && data.restaurants_included && (
                <p className="text-xs text-muted-foreground mt-2">
                    Restaurants inclus : {data.restaurants_included.join(', ')}
                </p>
            )}
        </div>
    );
};

// Custom tooltip pour l'histogramme ETP YTD
const EtpYtdChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const budgetData = payload.find(p => p.dataKey === 'Budget')?.payload || {};
        const realiseData = payload.find(p => p.dataKey === 'Réalisé')?.payload || {};
        
        const budget = payload.find(p => p.dataKey === 'Budget')?.value || 0;
        const realise = payload.find(p => p.dataKey === 'Réalisé')?.value || 0;
        const ecartTotal = realise - budget;
        const ecartPct = budget > 0 ? ((realise - budget) / budget * 100) : 0;
        
        // Écarts par département
        const ecartCuisine = (realiseData.realise_cuisine || 0) - (budgetData.budget_cuisine || 0);
        const ecartSalle = (realiseData.realise_service || 0) - (budgetData.budget_service || 0);
        
        const getEcartColor = (ecart) => ecart < 0 ? 'text-green-600' : ecart > 0 ? 'text-red-600' : 'text-yellow-600';
        
        return (
            <div className="bg-white border border-border rounded-lg shadow-lg p-3">
                <p className="font-semibold mb-2">{label}</p>
                <div className="space-y-1 text-sm">
                    <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Budget Total:</span>
                        <span className="font-medium">{budget.toFixed(2)} ETP</span>
                    </div>
                    <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Réalisé Total:</span>
                        <span className="font-medium">{realise.toFixed(2)} ETP</span>
                    </div>
                    <div className="pt-2 border-t mt-2">
                        <div className={cn("flex justify-between gap-4", getEcartColor(ecartCuisine))}>
                            <span>Écart Cuisine:</span>
                            <span className="font-bold">
                                {ecartCuisine >= 0 ? '+' : ''}{ecartCuisine.toFixed(2)} ETP
                            </span>
                        </div>
                        <div className={cn("flex justify-between gap-4", getEcartColor(ecartSalle))}>
                            <span>Écart Salle:</span>
                            <span className="font-bold">
                                {ecartSalle >= 0 ? '+' : ''}{ecartSalle.toFixed(2)} ETP
                            </span>
                        </div>
                    </div>
                    <div className={cn("flex justify-between gap-4 pt-2 border-t font-bold", getEcartColor(ecartTotal))}>
                        <span>Écart Total:</span>
                        <span>
                            {ecartTotal >= 0 ? '+' : ''}{ecartTotal.toFixed(2)} ETP ({ecartPct >= 0 ? '+' : ''}{ecartPct.toFixed(1)}%)
                        </span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

// Composant Histogramme ETP YTD (Réalisé vs Budget par mois)
const EtpYtdChart = ({ restaurantName, year }) => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const loadChartData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await effectifsApi.getYtdChart(year, restaurantName);
            setChartData(response.data);
        } catch (err) {
            console.error('Error loading YTD chart data:', err);
            setError('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [restaurantName, year]);
    
    useEffect(() => {
        loadChartData();
    }, [loadChartData]);
    
    if (loading) {
        return (
            <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">ETP YTD - Réalisé vs Budget</h3>
                </div>
                <Skeleton className="h-80 w-full" />
                <p className="text-sm text-muted-foreground text-center mt-4 animate-pulse">
                    Chargement des données mensuelles...
                </p>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">ETP YTD - Réalisé vs Budget</h3>
                </div>
                <div className="text-center py-8 text-muted-foreground">
                    {error}
                </div>
            </div>
        );
    }
    
    if (!chartData || !chartData.months || chartData.months.length === 0) {
        return null;
    }
    
    // Préparer les données pour le graphique (inclure cuisine/salle pour le tooltip)
    const data = chartData.months.map(m => ({
        name: m.month_label,
        month: m.month,
        'Budget': m.budget_total,
        'Réalisé': m.realise_total,
        budget_cuisine: m.budget_cuisine,
        budget_service: m.budget_service,
        realise_cuisine: m.realise_cuisine,
        realise_service: m.realise_service,
        ecart: m.ecart,
        ecart_pct: m.ecart_pct
    }));
    
    return (
        <div className="bg-card border border-border rounded-xl p-6" data-testid="etp-ytd-chart">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                        <BarChart3 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg">ETP YTD - Réalisé vs Budget</h3>
                        <p className="text-xs text-muted-foreground">
                            {chartData.restaurant} - {chartData.year}
                        </p>
                    </div>
                </div>
                
                <div className="flex items-center gap-3">
                    {/* Résumé YTD */}
                    {chartData.ytd_summary && (
                        <div className={cn(
                            "px-4 py-2 rounded-lg border text-sm",
                            chartData.ytd_summary.ecart < 0 ? "bg-green-50 border-green-200 text-green-700" :
                            chartData.ytd_summary.ecart > 0 ? "bg-red-50 border-red-200 text-red-700" :
                            "bg-yellow-50 border-yellow-200 text-yellow-700"
                        )}>
                            <span className="font-medium">YTD: </span>
                            <span className="font-bold">
                                {chartData.ytd_summary.ecart >= 0 ? '+' : ''}{chartData.ytd_summary.ecart.toFixed(2)} ETP 
                                ({chartData.ytd_summary.ecart_pct >= 0 ? '+' : ''}{chartData.ytd_summary.ecart_pct.toFixed(1)}%)
                            </span>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Graphique */}
            <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        barCategoryGap="20%"
                    >
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis 
                            dataKey="name" 
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                        />
                        <YAxis 
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                            label={{ value: 'ETP', angle: -90, position: 'insideLeft', fontSize: 12 }}
                        />
                        <Tooltip content={<EtpYtdChartTooltip />} />
                        <Legend 
                            wrapperStyle={{ paddingTop: '20px' }}
                        />
                        <ReferenceLine y={0} stroke="#666" />
                        <Bar 
                            dataKey="Budget" 
                            fill="#94a3b8" 
                            radius={[4, 4, 0, 0]}
                            name="Budget"
                        />
                        <Bar 
                            dataKey="Réalisé" 
                            fill="#3b82f6" 
                            radius={[4, 4, 0, 0]}
                            name="Réalisé"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

// Composant principal
const EffectifsPage = () => {
    const { user } = useAuth();
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [isConsolidatedView, setIsConsolidatedView] = useState(false);
    const [weekStart, setWeekStart] = useState(getMondayOfWeek());
    const [weeklyData, setWeeklyData] = useState(null);
    const [comparisonData, setComparisonData] = useState(null);
    const [consolidatedData, setConsolidatedData] = useState(null);
    const [consolidatedMonthlyData, setConsolidatedMonthlyData] = useState(null);
    const [ytdData, setYtdData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMonthly, setLoadingMonthly] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    
    // State pour le sélecteur de mois (ETP Mensuel)
    const now = new Date();
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    
    // Cache pour les données mensuelles ETP (clé: "month-year")
    const monthlyDataCacheRef = useRef({});
    
    // Ref pour éviter les race conditions lors des changements de vue
    const requestIdRef = useRef(0);

    // Charger les restaurants
    useEffect(() => {
        const loadRestaurants = async () => {
            try {
                const response = await restaurantsApi.getAll();
                const allRestaurants = response.data;
                setRestaurants(allRestaurants);
                
                // Ma biche sur le toit par défaut
                const maBiche = allRestaurants.find(r => r.name === "Ma biche sur le toit");
                if (maBiche) {
                    setSelectedRestaurant(maBiche);
                    setIsConsolidatedView(false);
                } else if (allRestaurants.length > 0) {
                    setSelectedRestaurant(allRestaurants[0]);
                    setIsConsolidatedView(false);
                }
            } catch (error) {
                console.error('Error loading restaurants:', error);
                toast.error('Erreur lors du chargement des restaurants');
            }
        };
        
        loadRestaurants();
    }, [user]);

    // Charger les données d'effectifs
    const loadEffectifsData = useCallback(async () => {
        // Ne pas charger si pas en vue consolidée et pas de restaurant sélectionné
        if (!isConsolidatedView && !selectedRestaurant) {
            setLoading(false);
            return;
        }
        
        // Incrémenter l'ID de requête pour éviter les race conditions
        const currentRequestId = ++requestIdRef.current;
        
        setLoading(true);
        
        try {
            if (isConsolidatedView) {
                // Vue consolidée : récupérer tous les restaurants
                // Note: Utilise fetch natif car axios a des problèmes de timeout avec cette API lente
                const token = localStorage.getItem('token');
                const apiUrl = process.env.REACT_APP_BACKEND_URL;
                const fetchResponse = await fetch(`${apiUrl}/api/effectifs/all-restaurants?week_start=${weekStart}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                if (!fetchResponse.ok) {
                    throw new Error(`HTTP error! status: ${fetchResponse.status}`);
                }
                
                const data = await fetchResponse.json();
                const response = { data };
                
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }
                
                setConsolidatedData(response.data);
                setWeeklyData(null);
                setComparisonData(null);
                setYtdData(null);
                setLoading(false);
                return;
            } else if (selectedRestaurant) {
                // Vue individuelle - charger weekly, comparison et YTD
                console.log('Loading individual data for:', selectedRestaurant.name, 'week:', weekStart);
                try {
                    const [weeklyRes, comparisonRes, ytdRes] = await Promise.all([
                        effectifsApi.getWeekly(selectedRestaurant.name, weekStart),
                        effectifsApi.getComparison(selectedRestaurant.name, weekStart),
                        effectifsApi.getYtd(selectedRestaurant.name, weekStart)
                    ]);
                    console.log('Individual data received:', { 
                        weekly: weeklyRes?.data, 
                        comparison: comparisonRes?.data, 
                        ytd: ytdRes?.data,
                        currentRequestId,
                        refValue: requestIdRef.current
                    });
                    
                    // Ignorer la réponse si une nouvelle requête a été lancée
                    if (currentRequestId !== requestIdRef.current) {
                        console.log('Ignoring stale response');
                        return;
                    }
                    
                    setWeeklyData(weeklyRes.data);
                    setComparisonData(comparisonRes.data);
                    setYtdData(ytdRes.data);
                    setConsolidatedData(null);
                } catch (fetchError) {
                    console.error('Fetch error:', fetchError);
                    throw fetchError;
                }
            }
        } catch (error) {
            // Ignorer les erreurs si une nouvelle requête a été lancée
            if (currentRequestId !== requestIdRef.current) return;
            
            console.error('Error loading effectifs data:', error);
            toast.error('Erreur lors du chargement des données');
            setWeeklyData(null);
            setComparisonData(null);
            setConsolidatedData(null);
            setYtdData(null);
        } finally {
            // Ne définir loading=false que si c'est toujours la requête actuelle
            if (currentRequestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    }, [selectedRestaurant, weekStart, isConsolidatedView]);

    useEffect(() => {
        loadEffectifsData();
    }, [loadEffectifsData]);

    // Fonction pour charger les données mensuelles (avec cache)
    const loadMonthlyData = useCallback((month, year) => {
        const cacheKey = `${month}-${year}`;
        
        // Vérifier si les données sont en cache
        if (monthlyDataCacheRef.current[cacheKey]) {
            console.log('Monthly data loaded from cache:', cacheKey);
            setConsolidatedMonthlyData(monthlyDataCacheRef.current[cacheKey]);
            return;
        }
        
        console.log('Starting monthly data fetch for month:', month, 'year:', year);
        setLoadingMonthly(true);
        setConsolidatedMonthlyData(null); // Reset pendant le chargement
        effectifsApi.getAllRestaurantsMonthly(month, year)
            .then(monthlyResponse => {
                console.log('Monthly data received:', monthlyResponse.data);
                // Sauvegarder en cache
                monthlyDataCacheRef.current[cacheKey] = monthlyResponse.data;
                setConsolidatedMonthlyData(monthlyResponse.data);
            })
            .catch(monthlyError => {
                console.error('Error loading monthly data:', monthlyError);
            })
            .finally(() => {
                console.log('Monthly loading finished');
                setLoadingMonthly(false);
            });
    }, []);

    // Recharger les données mensuelles quand le mois sélectionné change ou quand on passe en vue consolidée
    useEffect(() => {
        if (isConsolidatedView && !loading && consolidatedData) {
            loadMonthlyData(selectedMonth, selectedYear);
        }
    }, [selectedMonth, selectedYear, isConsolidatedView, loading, consolidatedData, loadMonthlyData]);

    // Gestion du changement de mois (ETP Mensuel)
    const handleMonthChange = (value) => {
        const [month, year] = value.split('-').map(Number);
        setSelectedMonth(month);
        setSelectedYear(year);
    };

    // Générer la liste des mois disponibles (uniquement 2026)
    const getAvailableMonths = () => {
        const months = [];
        const currentDate = new Date();
        const currentYear = 2026;
        const currentMonth = currentDate.getFullYear() === currentYear ? currentDate.getMonth() : 11;
        
        // Du mois actuel jusqu'à janvier 2026
        for (let m = currentMonth; m >= 0; m--) {
            const d = new Date(currentYear, m, 1);
            months.push({
                value: `${d.getMonth() + 1}-${d.getFullYear()}`,
                label: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
            });
        }
        return months;
    };

    // Gestion du changement de restaurant
    const handleRestaurantChange = (value) => {
        if (value === 'consolidated') {
            setIsConsolidatedView(true);
            setSelectedRestaurant(null);
        } else {
            setIsConsolidatedView(false);
            const resto = restaurants.find(r => r.name === value);
            if (resto) setSelectedRestaurant(resto);
        }
    };

    // Navigation semaine
    const goToPreviousWeek = () => {
        const current = new Date(weekStart);
        current.setDate(current.getDate() - 7);
        setWeekStart(current.toISOString().split('T')[0]);
    };

    const goToNextWeek = () => {
        const current = new Date(weekStart);
        current.setDate(current.getDate() + 7);
        setWeekStart(current.toISOString().split('T')[0]);
    };

    const goToCurrentWeek = () => {
        setWeekStart(getMondayOfWeek());
    };

    return (
        <div className="flex h-screen bg-background">
            <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
            
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
                
                <main className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-7xl mx-auto space-y-6">
                        {/* Titre */}
                        <div className="flex flex-col gap-2">
                            <h1 className="text-2xl font-heading font-bold">Pilotage des Effectifs</h1>
                            <p className="text-muted-foreground">
                                Suivi des ETP planifiés vs budgétés par département
                            </p>
                        </div>
                        
                        {/* Sélecteur de restaurant avec logos - visible pour tous les utilisateurs avec restaurants */}
                        {restaurants.length > 0 && (
                            <div className="bg-card border border-border rounded-xl p-4">
                                <RestaurantSelector
                                    restaurants={restaurants}
                                    selectedId={selectedRestaurant?.id}
                                    onSelect={(resto) => handleRestaurantChange(resto.name)}
                                    showConsolidated={user?.role === 'admin' || user?.role === 'direction' || (user?.has_consolidated_access && restaurants.length > 1)}
                                    isConsolidated={isConsolidatedView}
                                    onConsolidatedSelect={() => handleRestaurantChange('consolidated')}
                                />
                            </div>
                        )}
                        
                        {/* Afficher le nom du restaurant pour les directeurs avec un seul restaurant */}
                        {restaurants.length === 1 && selectedRestaurant && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
                                <Building2 className="w-5 h-5 text-primary" />
                                <span className="font-medium">{selectedRestaurant.name}</span>
                            </div>
                        )}

                        {/* Navigation semaine */}
                        <div className="bg-card border border-border rounded-xl p-4">
                            <div className="flex items-center justify-between">
                                <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={goToPreviousWeek}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                
                                <div className="flex items-center gap-4">
                                    <Calendar className="w-5 h-5 text-muted-foreground" />
                                    <div className="text-center">
                                        <p className="font-semibold">{formatWeekRange(weekStart)}</p>
                                        <p className="text-xs text-muted-foreground">Semaine sélectionnée</p>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={goToCurrentWeek}
                                    >
                                        Aujourd&apos;hui
                                    </Button>
                                </div>
                                
                                <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={goToNextWeek}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Cartes ETP ou Vue consolidée */}
                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[1, 2, 3].map(i => (
                                    <Skeleton key={i} className="h-40 rounded-xl" />
                                ))}
                            </div>
                        ) : isConsolidatedView ? (
                            /* VUE CONSOLIDÉE */
                            <div className="space-y-6">
                                {/* Tableau hebdomadaire */}
                                <div className="bg-card border border-border rounded-xl overflow-hidden">
                                    <div className="p-4 border-b border-border bg-muted/30">
                                        <h2 className="font-semibold flex items-center gap-2">
                                            <Calendar className="w-5 h-5 text-primary" />
                                            ETP Hebdomadaire - Semaine du {formatWeekRange(weekStart)}
                                        </h2>
                                        <p className="text-sm text-muted-foreground mt-1">Comparaison des ETP planifiés vs budget pour la semaine sélectionnée</p>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-border bg-muted/20">
                                                    <th className="text-left py-3 px-4 text-sm font-medium">Restaurant</th>
                                                    <th className="text-center py-3 px-4 text-sm font-medium text-orange-500">Cuisine (ETP)</th>
                                                    <th className="text-center py-3 px-4 text-sm font-medium text-blue-500">Service (ETP)</th>
                                                    <th className="text-center py-3 px-4 text-sm font-medium">Total (ETP)</th>
                                                    <th className="text-center py-3 px-4 text-sm font-medium">Budget Total</th>
                                                    <th className="text-center py-3 px-4 text-sm font-medium text-orange-500">Écart Cuisine</th>
                                                    <th className="text-center py-3 px-4 text-sm font-medium text-blue-500">Écart Salle</th>
                                                    <th className="text-center py-3 px-4 text-sm font-medium">Écart Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {consolidatedData?.restaurants?.map((resto, idx) => {
                                                    const varianceCuisine = resto.planned.cuisine - resto.budget.cuisine;
                                                    const varianceService = resto.planned.service - resto.budget.service;
                                                    const varianceTotal = resto.planned.total - resto.budget.total;
                                                    
                                                    const getColor = (v) => v < -0.01 ? 'text-green-600' : v > 0.01 ? 'text-red-600' : 'text-yellow-600';
                                                    const getBg = (v) => v < -0.01 ? 'bg-green-50' : v > 0.01 ? 'bg-red-50' : 'bg-yellow-50';
                                                    
                                                    const formatVariance = (v, budget) => {
                                                        if (budget === 0) return '-';
                                                        const pct = budget > 0 ? (v / budget * 100) : 0;
                                                        return `${v >= 0 ? '+' : ''}${v.toFixed(2)} (${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%)`;
                                                    };
                                                    
                                                    return (
                                                        <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                                                            <td className="py-3 px-4 font-medium">{resto.restaurant}</td>
                                                            <td className="text-center py-3 px-4 text-orange-600 font-medium">
                                                                {resto.planned.cuisine.toFixed(2)}
                                                            </td>
                                                            <td className="text-center py-3 px-4 text-blue-600 font-medium">
                                                                {resto.planned.service.toFixed(2)}
                                                            </td>
                                                            <td className="text-center py-3 px-4 font-bold">
                                                                {resto.planned.total.toFixed(2)}
                                                            </td>
                                                            <td className="text-center py-3 px-4 text-muted-foreground">
                                                                {resto.budget.total.toFixed(2)}
                                                            </td>
                                                            <td className={cn("text-center py-3 px-4 font-medium", getColor(varianceCuisine))}>
                                                                <span className={cn("px-2 py-0.5 rounded text-xs", getBg(varianceCuisine))}>
                                                                    {formatVariance(varianceCuisine, resto.budget.cuisine)}
                                                                </span>
                                                            </td>
                                                            <td className={cn("text-center py-3 px-4 font-medium", getColor(varianceService))}>
                                                                <span className={cn("px-2 py-0.5 rounded text-xs", getBg(varianceService))}>
                                                                    {formatVariance(varianceService, resto.budget.service)}
                                                                </span>
                                                            </td>
                                                            <td className={cn("text-center py-3 px-4 font-bold", getColor(varianceTotal))}>
                                                                <span className={cn("px-2 py-1 rounded", getBg(varianceTotal))}>
                                                                    {formatVariance(varianceTotal, resto.budget.total)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {/* Ligne de total */}
                                                {consolidatedData?.restaurants && (
                                                    <tr className="bg-muted/50 font-bold">
                                                        <td className="py-3 px-4">TOTAL</td>
                                                        <td className="text-center py-3 px-4 text-orange-600">
                                                            {consolidatedData.restaurants.reduce((sum, r) => sum + r.planned.cuisine, 0).toFixed(2)}
                                                        </td>
                                                        <td className="text-center py-3 px-4 text-blue-600">
                                                            {consolidatedData.restaurants.reduce((sum, r) => sum + r.planned.service, 0).toFixed(2)}
                                                        </td>
                                                        <td className="text-center py-3 px-4">
                                                            {consolidatedData.restaurants.reduce((sum, r) => sum + r.planned.total, 0).toFixed(2)}
                                                        </td>
                                                        <td className="text-center py-3 px-4 text-muted-foreground">
                                                            {consolidatedData.restaurants.reduce((sum, r) => sum + r.budget.total, 0).toFixed(2)}
                                                        </td>
                                                        {/* Écart Cuisine Total */}
                                                        <td className="text-center py-3 px-4">
                                                            {(() => {
                                                                const plannedCuisine = consolidatedData.restaurants.reduce((sum, r) => sum + r.planned.cuisine, 0);
                                                                const budgetCuisine = consolidatedData.restaurants.reduce((sum, r) => sum + r.budget.cuisine, 0);
                                                                const v = plannedCuisine - budgetCuisine;
                                                                const pct = budgetCuisine > 0 ? (v / budgetCuisine * 100) : 0;
                                                                const color = v < -0.01 ? 'text-green-600 bg-green-50' : v > 0.01 ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50';
                                                                return (
                                                                    <span className={cn("px-2 py-0.5 rounded text-xs", color)}>
                                                                        {v >= 0 ? '+' : ''}{v.toFixed(2)} ({pct >= 0 ? '+' : ''}{pct.toFixed(0)}%)
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        {/* Écart Salle Total */}
                                                        <td className="text-center py-3 px-4">
                                                            {(() => {
                                                                const plannedService = consolidatedData.restaurants.reduce((sum, r) => sum + r.planned.service, 0);
                                                                const budgetService = consolidatedData.restaurants.reduce((sum, r) => sum + r.budget.service, 0);
                                                                const v = plannedService - budgetService;
                                                                const pct = budgetService > 0 ? (v / budgetService * 100) : 0;
                                                                const color = v < -0.01 ? 'text-green-600 bg-green-50' : v > 0.01 ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50';
                                                                return (
                                                                    <span className={cn("px-2 py-0.5 rounded text-xs", color)}>
                                                                        {v >= 0 ? '+' : ''}{v.toFixed(2)} ({pct >= 0 ? '+' : ''}{pct.toFixed(0)}%)
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        {/* Écart Total */}
                                                        <td className="text-center py-3 px-4">
                                                            {(() => {
                                                                const totalPlanned = consolidatedData.restaurants.reduce((sum, r) => sum + r.planned.total, 0);
                                                                const totalBudget = consolidatedData.restaurants.reduce((sum, r) => sum + r.budget.total, 0);
                                                                const v = totalPlanned - totalBudget;
                                                                const pct = totalBudget > 0 ? (v / totalBudget * 100) : 0;
                                                                const color = v < -0.01 ? 'text-green-600 bg-green-50' : v > 0.01 ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50';
                                                                return (
                                                                    <span className={cn("px-2 py-1 rounded", color)}>
                                                                        {v >= 0 ? '+' : ''}{v.toFixed(2)} ({pct >= 0 ? '+' : ''}{pct.toFixed(0)}%)
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                                {/* Tableau mensuel */}
                                <div className="bg-card border border-border rounded-xl overflow-hidden">
                                    <div className="p-4 border-b border-border bg-blue-50/50">
                                        <div className="flex items-center justify-between flex-wrap gap-3">
                                            <div>
                                                <h2 className="font-semibold flex items-center gap-2">
                                                    <TrendingUp className="w-5 h-5 text-blue-600" />
                                                    ETP Mensuel
                                                </h2>
                                                <p className="text-sm text-muted-foreground mt-1">Moyenne des ETP sur toutes les semaines du mois vs budget mensuel</p>
                                            </div>
                                            <Select
                                                value={`${selectedMonth}-${selectedYear}`}
                                                onValueChange={handleMonthChange}
                                            >
                                                <SelectTrigger className="w-[180px] bg-white" data-testid="month-selector">
                                                    <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {getAvailableMonths().map((m) => (
                                                        <SelectItem key={m.value} value={m.value}>
                                                            {m.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    {loadingMonthly ? (
                                        <div className="p-8 text-center">
                                            <Skeleton className="h-40 w-full mb-4" />
                                            <p className="text-sm text-muted-foreground animate-pulse">
                                                Chargement des données mensuelles (~30 secondes)...
                                            </p>
                                        </div>
                                    ) : consolidatedMonthlyData?.restaurants ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-border bg-blue-50/30">
                                                        <th className="text-left py-3 px-4 text-sm font-medium">Restaurant</th>
                                                        <th className="text-center py-3 px-4 text-sm font-medium text-orange-500">Cuisine (ETP)</th>
                                                        <th className="text-center py-3 px-4 text-sm font-medium text-blue-500">Service (ETP)</th>
                                                        <th className="text-center py-3 px-4 text-sm font-medium">Total (ETP)</th>
                                                        <th className="text-center py-3 px-4 text-sm font-medium">Budget Total</th>
                                                        <th className="text-center py-3 px-4 text-sm font-medium text-orange-500">Écart Cuisine</th>
                                                        <th className="text-center py-3 px-4 text-sm font-medium text-blue-500">Écart Salle</th>
                                                        <th className="text-center py-3 px-4 text-sm font-medium">Écart Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {consolidatedMonthlyData.restaurants.map((resto, idx) => {
                                                        const varianceCuisine = resto.planned.cuisine - resto.budget.cuisine;
                                                        const varianceService = resto.planned.service - resto.budget.service;
                                                        const varianceTotal = resto.planned.total - resto.budget.total;
                                                        
                                                        const getColor = (v) => v < -0.01 ? 'text-green-600' : v > 0.01 ? 'text-red-600' : 'text-yellow-600';
                                                        const getBg = (v) => v < -0.01 ? 'bg-green-50' : v > 0.01 ? 'bg-red-50' : 'bg-yellow-50';
                                                        
                                                        const formatVariance = (v, budget) => {
                                                            if (budget === 0) return '-';
                                                            const pct = budget > 0 ? (v / budget * 100) : 0;
                                                            return `${v >= 0 ? '+' : ''}${v.toFixed(2)} (${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%)`;
                                                        };
                                                        
                                                        return (
                                                            <tr key={idx} className="border-b border-border/50 hover:bg-blue-50/20">
                                                                <td className="py-3 px-4 font-medium">{resto.restaurant}</td>
                                                                <td className="text-center py-3 px-4 text-orange-600 font-medium">
                                                                    {resto.planned.cuisine.toFixed(2)}
                                                                </td>
                                                                <td className="text-center py-3 px-4 text-blue-600 font-medium">
                                                                    {resto.planned.service.toFixed(2)}
                                                                </td>
                                                                <td className="text-center py-3 px-4 font-bold">
                                                                    {resto.planned.total.toFixed(2)}
                                                                </td>
                                                                <td className="text-center py-3 px-4 text-muted-foreground">
                                                                    {resto.budget.total.toFixed(2)}
                                                                </td>
                                                                <td className={cn("text-center py-3 px-4 font-medium", getColor(varianceCuisine))}>
                                                                    <span className={cn("px-2 py-0.5 rounded text-xs", getBg(varianceCuisine))}>
                                                                        {formatVariance(varianceCuisine, resto.budget.cuisine)}
                                                                    </span>
                                                                </td>
                                                                <td className={cn("text-center py-3 px-4 font-medium", getColor(varianceService))}>
                                                                    <span className={cn("px-2 py-0.5 rounded text-xs", getBg(varianceService))}>
                                                                        {formatVariance(varianceService, resto.budget.service)}
                                                                    </span>
                                                                </td>
                                                                <td className={cn("text-center py-3 px-4 font-bold", getColor(varianceTotal))}>
                                                                    <span className={cn("px-2 py-1 rounded", getBg(varianceTotal))}>
                                                                        {formatVariance(varianceTotal, resto.budget.total)}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {/* Ligne de total mensuel */}
                                                    <tr className="bg-blue-100/50 font-bold">
                                                        <td className="py-3 px-4">TOTAL MENSUEL</td>
                                                        <td className="text-center py-3 px-4 text-orange-600">
                                                            {consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.planned.cuisine, 0).toFixed(2)}
                                                        </td>
                                                        <td className="text-center py-3 px-4 text-blue-600">
                                                            {consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.planned.service, 0).toFixed(2)}
                                                        </td>
                                                        <td className="text-center py-3 px-4">
                                                            {consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.planned.total, 0).toFixed(2)}
                                                        </td>
                                                        <td className="text-center py-3 px-4 text-muted-foreground">
                                                            {consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.budget.total, 0).toFixed(2)}
                                                        </td>
                                                        <td className="text-center py-3 px-4">
                                                            {(() => {
                                                                const plannedCuisine = consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.planned.cuisine, 0);
                                                                const budgetCuisine = consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.budget.cuisine, 0);
                                                                const v = plannedCuisine - budgetCuisine;
                                                                const pct = budgetCuisine > 0 ? (v / budgetCuisine * 100) : 0;
                                                                const color = v < -0.01 ? 'text-green-600 bg-green-50' : v > 0.01 ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50';
                                                                return (
                                                                    <span className={cn("px-2 py-0.5 rounded text-xs", color)}>
                                                                        {v >= 0 ? '+' : ''}{v.toFixed(2)} ({pct >= 0 ? '+' : ''}{pct.toFixed(0)}%)
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td className="text-center py-3 px-4">
                                                            {(() => {
                                                                const plannedService = consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.planned.service, 0);
                                                                const budgetService = consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.budget.service, 0);
                                                                const v = plannedService - budgetService;
                                                                const pct = budgetService > 0 ? (v / budgetService * 100) : 0;
                                                                const color = v < -0.01 ? 'text-green-600 bg-green-50' : v > 0.01 ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50';
                                                                return (
                                                                    <span className={cn("px-2 py-0.5 rounded text-xs", color)}>
                                                                        {v >= 0 ? '+' : ''}{v.toFixed(2)} ({pct >= 0 ? '+' : ''}{pct.toFixed(0)}%)
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td className="text-center py-3 px-4">
                                                            {(() => {
                                                                const totalPlanned = consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.planned.total, 0);
                                                                const totalBudget = consolidatedMonthlyData.restaurants.reduce((sum, r) => sum + r.budget.total, 0);
                                                                const v = totalPlanned - totalBudget;
                                                                const pct = totalBudget > 0 ? (v / totalBudget * 100) : 0;
                                                                const color = v < -0.01 ? 'text-green-600 bg-green-50' : v > 0.01 ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50';
                                                                return (
                                                                    <span className={cn("px-2 py-1 rounded", color)}>
                                                                        {v >= 0 ? '+' : ''}{v.toFixed(2)} ({pct >= 0 ? '+' : ''}{pct.toFixed(0)}%)
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="p-8 text-center text-muted-foreground">
                                            Données mensuelles non disponibles
                                        </div>
                                    )}
                                </div>
                                
                                {/* Tableau présence des postes à responsabilité - Vue consolidée */}
                                <div className="bg-card border border-border rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-4">
                                        <ChefHat className="w-5 h-5 text-primary" />
                                        <div>
                                            <h2 className="font-semibold">Présence des postes à responsabilité</h2>
                                            <p className="text-xs text-muted-foreground">Managers, Chefs, Responsables - Tous restaurants</p>
                                        </div>
                                    </div>
                                    
                                    <ResponsibilityPresenceTable 
                                        weekStart={weekStart}
                                        isConsolidated={true}
                                    />
                                </div>
                                
                                {/* Tableau présence à l'heure de pointe - Vue consolidée */}
                                <div className="bg-card border border-border rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Users className="w-5 h-5 text-primary" />
                                        <div>
                                            <h2 className="font-semibold">Effectifs à l'heure de pointe</h2>
                                            <p className="text-xs text-muted-foreground">Nombre de personnes présentes aux heures clés - Tous restaurants</p>
                                        </div>
                                    </div>
                                    
                                    <PeakHourPresenceTable 
                                        weekStart={weekStart}
                                        isConsolidated={true}
                                    />
                                </div>
                                
                                {/* Histogramme ETP YTD */}
                                <EtpYtdChart 
                                    restaurantName={null} 
                                    year={selectedYear} 
                                />
                            </div>
                        ) : (
                            /* VUE INDIVIDUELLE */
                            <div className="space-y-6">
                                {/* Alerte données incomplètes */}
                                <DataWarningAlert comparisonData={comparisonData} />
                                
                                {/* Cartes ETP de la semaine */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <EtpCard
                                        title="Cuisine"
                                        planned={comparisonData?.planned?.cuisine}
                                        budget={comparisonData?.budget?.cuisine}
                                        variancePct={comparisonData?.variance_pct?.cuisine}
                                        varianceEtp={comparisonData?.variance_etp?.cuisine}
                                        icon={ChefHat}
                                        color="cuisine"
                                    />
                                    <EtpCard
                                        title="Service (Salle)"
                                        planned={comparisonData?.planned?.service}
                                        budget={comparisonData?.budget?.service}
                                        variancePct={comparisonData?.variance_pct?.service}
                                        varianceEtp={comparisonData?.variance_etp?.service}
                                        icon={UtensilsCrossed}
                                        color="service"
                                    />
                                    <EtpCard
                                        title="Total"
                                        planned={comparisonData?.planned?.total}
                                        budget={comparisonData?.budget?.total}
                                        variancePct={comparisonData?.variance_pct?.total}
                                        varianceEtp={comparisonData?.variance_etp?.total}
                                        icon={Users}
                                        color="primary"
                                    />
                                </div>
                                
                                {/* Carte YTD ETP */}
                                <YtdEtpCard ytdData={ytdData} loading={loading} />
                            </div>
                        )}

                        {/* Tableau présence des postes à responsabilité - Vue individuelle */}
                        {!isConsolidatedView && selectedRestaurant && (
                        <div className="bg-card border border-border rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <ChefHat className="w-5 h-5 text-primary" />
                                <div>
                                    <h2 className="font-semibold">Présence des postes à responsabilité</h2>
                                    <p className="text-xs text-muted-foreground">Managers, Chefs, Responsables de {selectedRestaurant.name}</p>
                                </div>
                            </div>
                            
                            <ResponsibilityPresenceTable 
                                restaurantName={selectedRestaurant.name} 
                                weekStart={weekStart}
                                isConsolidated={false}
                            />
                        </div>
                        )}

                        {/* Tableau présence à l'heure de pointe - Vue individuelle uniquement */}
                        {!isConsolidatedView && selectedRestaurant && (
                        <div className="bg-card border border-border rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <Users className="w-5 h-5 text-primary" />
                                <div>
                                    <h2 className="font-semibold">Effectifs à l'heure de pointe</h2>
                                    <p className="text-xs text-muted-foreground">Nombre de personnes présentes aux heures clés de chaque service</p>
                                </div>
                            </div>
                            
                            <PeakHourPresenceTable 
                                restaurantName={selectedRestaurant.name} 
                                weekStart={weekStart}
                                isConsolidated={false}
                            />
                        </div>
                        )}

                        {/* Histogramme ETP YTD - Vue individuelle uniquement */}
                        {!isConsolidatedView && selectedRestaurant && (
                            <EtpYtdChart 
                                restaurantName={selectedRestaurant.name} 
                                year={new Date().getFullYear()} 
                            />
                        )}

                        {/* Info sur le calcul */}
                        <div className="bg-muted/30 border border-border rounded-xl p-4 text-sm text-muted-foreground">
                            <p><strong>Calcul ETP :</strong> (Heures contrat - Absences) / 35 + forfaits jour (1,2 ETP)</p>
                            <p className="mt-2">
                                <strong>Inclus :</strong> Heures contrat des employés présents (shifts ou CP).
                                <br/>
                                <strong>Exclus :</strong> Arrêts maladie, absences justifiées/injustifiées.
                            </p>
                            <p className="mt-2"><strong>Légende écarts :</strong> <span className="text-green-600">Vert = sous budget</span> | <span className="text-yellow-600">Jaune = égal</span> | <span className="text-red-600">Rouge = dépassement</span></p>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default EffectifsPage;
